
import { useState } from 'react';

const THEMES = [
  { id:'dark',    label:'Dark',     bg:'#080f1e', surface:'#0f1e38', border:'#1e3a6e', text:'#e8edf8', sub:'#8ba3cc' },
  { id:'darker',  label:'Darker',   bg:'#030810', surface:'#08121f', border:'#162848', text:'#e8edf8', sub:'#6a8ab0' },
  { id:'midnight',label:'Midnight', bg:'#000000', surface:'#0a0a0a', border:'#222222', text:'#f0f0f0', sub:'#888888' },
  { id:'slate',   label:'Slate',    bg:'#0d1117', surface:'#161b22', border:'#30363d', text:'#e6edf3', sub:'#8b949e' },
  { id:'navy',    label:'Navy',     bg:'#050d1f', surface:'#0a1a38', border:'#1a3060', text:'#dce8ff', sub:'#7090c0' },
  { id:'light',   label:'Light',    bg:'#f0f4f8', surface:'#ffffff', border:'#d0d8e4', text:'#1a2030', sub:'#5a6a8a' },
  { id:'paper',   label:'Paper',    bg:'#faf8f4', surface:'#ffffff', border:'#e0d8cc', text:'#2a2010', sub:'#7a6a50' },
];

const FONT_SIZES = [
  { id:'sm',  label:'Small',   scale:0.9 },
  { id:'md',  label:'Medium',  scale:1.0 },
  { id:'lg',  label:'Large',   scale:1.12 },
  { id:'xl',  label:'X-Large', scale:1.25 },
];

export function applyTheme(themeId, fontSizeId) {
  const theme = THEMES.find(t => t.id === themeId) || THEMES[0];
  const font  = FONT_SIZES.find(f => f.id === fontSizeId) || FONT_SIZES[1];
  const root  = document.documentElement;
  root.style.setProperty('--bg',      theme.bg);
  root.style.setProperty('--surface', theme.surface);
  root.style.setProperty('--border',  theme.border);
  root.style.setProperty('--text',    theme.text);
  root.style.setProperty('--sub',     theme.sub);
  root.style.setProperty('--scale',   font.scale);
  // Persist
  try { localStorage.setItem('sa_theme', themeId); localStorage.setItem('sa_font', fontSizeId); } catch(e) {}
}

export function loadSavedTheme() {
  try {
    const t = localStorage.getItem('sa_theme') || 'dark';
    const f = localStorage.getItem('sa_font')  || 'md';
    return { themeId: t, fontSizeId: f };
  } catch(e) { return { themeId:'dark', fontSizeId:'md' }; }
}

export default function Settings({ onClose }) {
  const saved = loadSavedTheme();
  const [theme,    setTheme]    = useState(saved.themeId);
  const [fontSize, setFontSize] = useState(saved.fontSizeId);

  const apply = (t, f) => {
    setTheme(t); setFontSize(f);
    applyTheme(t, f);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{background:'var(--surface,#0f1e38)',border:'1px solid var(--border,#1e3a6e)',borderRadius:16,
        padding:'22px 20px',width:'100%',maxWidth:360,display:'flex',flexDirection:'column',gap:18}}>

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.4rem',color:'#f5c518',letterSpacing:'0.08em'}}>
            ⚙️ SETTINGS
          </span>
          <button onClick={onClose}
            style={{background:'transparent',border:'1px solid var(--border,#1e3a6e)',borderRadius:7,
              color:'var(--sub,#8ba3cc)',padding:'4px 10px',cursor:'pointer',fontSize:'0.75rem',fontFamily:'Inter,sans-serif'}}>
            Close
          </button>
        </div>

        {/* Theme */}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'var(--sub,#8ba3cc)',textTransform:'uppercase'}}>
            Colour Theme
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6}}>
            {THEMES.map(t => (
              <button key={t.id} onClick={() => apply(t.id, fontSize)}
                style={{padding:'8px 6px',borderRadius:9,border:`2px solid ${theme===t.id?'#f5c518':'var(--border,#1e3a6e)'}`,
                  background: t.bg, color: t.text, cursor:'pointer',fontSize:'0.72rem',
                  fontFamily:'Inter,sans-serif',fontWeight:600,transition:'border-color 0.15s',
                  display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                <div style={{width:28,height:14,borderRadius:4,background:t.surface,border:`1px solid ${t.border}`}}/>
                {t.label}
                {theme === t.id && <span style={{fontSize:'0.6rem',color:'#f5c518'}}>✓ Active</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Font size */}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'var(--sub,#8ba3cc)',textTransform:'uppercase'}}>
            Text Size
          </div>
          <div style={{display:'flex',gap:6}}>
            {FONT_SIZES.map(f => (
              <button key={f.id} onClick={() => apply(theme, f.id)}
                style={{flex:1,padding:'9px 4px',borderRadius:9,
                  border:`2px solid ${fontSize===f.id?'#f5c518':'var(--border,#1e3a6e)'}`,
                  background:'transparent',color: fontSize===f.id?'#f5c518':'var(--sub,#8ba3cc)',
                  cursor:'pointer',fontFamily:'Inter,sans-serif',
                  fontSize: f.scale*0.75+'rem',fontWeight:600,transition:'0.15s'}}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contrast helper */}
        <div style={{fontSize:'0.65rem',color:'var(--sub,#8ba3cc)',lineHeight:1.6,
          borderTop:'1px solid var(--border,#1e3a6e)',paddingTop:12}}>
          <strong style={{color:'var(--text,#e8edf8)'}}>Tip:</strong> Try <em>Large</em> or <em>X-Large</em> text with the <em>Slate</em> theme for the best readability.
        </div>
      </div>
    </div>
  );
}
